import React, { useRef, useState, useLayoutEffect, useImperativeHandle, forwardRef, useEffect } from 'react'
import { renderToStaticMarkup } from 'react-dom/server';

import { backendFunction } from 'utils/backendApi';

import MarkdownRenderer from 'components/markdown/MarkdownRenderer';

import { useUser } from 'utils/userContext';

export const AIExplainIcon = () => {
    return <svg width="23" height="23" viewBox="0 0 54 59" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.2854 9.52804C20.88 4.86137 27.328 4.72004 29.2187 9.10404L29.3787 9.53071L31.5307 15.824C32.0239 17.2673 32.8209 18.5881 33.8678 19.6972C34.9148 20.8063 36.1875 21.6779 37.6 22.2534L38.1787 22.4694L44.472 24.6187C49.1387 26.2134 49.28 32.6614 44.8987 34.552L44.472 34.712L38.1787 36.864C36.7349 37.3569 35.4137 38.1537 34.3041 39.2007C33.1945 40.2477 32.3224 41.5206 31.7467 42.9334L31.5307 43.5094L29.3814 49.8054C27.7867 54.472 21.3387 54.6134 19.4507 50.232L19.2854 49.8054L17.136 43.512C16.6432 42.0683 15.8464 40.747 14.7993 39.6374C13.7523 38.5278 12.4795 37.6558 11.0667 37.08L10.4907 36.864L4.19737 34.7147C-0.471966 33.12 -0.613299 26.672 3.7707 24.784L4.19737 24.6187L10.4907 22.4694C11.934 21.9762 13.2547 21.1792 14.3638 20.1322C15.4729 19.0852 16.3446 17.8125 16.92 16.4L17.136 15.824L19.2854 9.52804ZM45.6667 0.333375C46.1656 0.333373 46.6545 0.473316 47.0778 0.737298C47.5011 1.00128 47.8419 1.37871 48.0614 1.82671L48.1894 2.13871L49.1227 4.87471L51.8614 5.80804C52.3613 5.9779 52.7996 6.29235 53.1206 6.71155C53.4417 7.13075 53.6311 7.63582 53.6648 8.16276C53.6985 8.6897 53.575 9.21479 53.31 9.67147C53.0449 10.1282 52.6503 10.4959 52.176 10.728L51.8614 10.856L49.1254 11.7894L48.192 14.528C48.0219 15.0279 47.7072 15.4659 47.2878 15.7867C46.8685 16.1075 46.3633 16.2966 45.8364 16.33C45.3095 16.3634 44.7845 16.2396 44.328 15.9744C43.8715 15.7091 43.504 15.3144 43.272 14.84L43.144 14.528L42.2107 11.792L39.472 10.8587C38.9721 10.6889 38.5338 10.3744 38.2128 9.9552C37.8917 9.536 37.7023 9.03093 37.6686 8.50399C37.6349 7.97705 37.7584 7.45196 38.0235 6.99528C38.2885 6.53859 38.6831 6.17087 39.1574 5.93871L39.472 5.81071L42.208 4.87737L43.1414 2.13871C43.3212 1.61184 43.6614 1.15446 44.1143 0.830704C44.5671 0.506945 45.11 0.33304 45.6667 0.333375Z" fill="var(--primary)" />
    </svg>
}

export const toPlainText = el =>
    renderToStaticMarkup(el)          // <span>Hello <em>world</em></span>
        .replace(/<[^>]+>/g, '')       // strip tags → "Hello world"
        .trim();

const AIExplanation = forwardRef(({ }, ref) => {
    const [loadingAi, setLoadingAi] = useState(false);
    const [aiResponse, setAiResponse] = useState(null);
    const [showingSection, setShowingSection] = useState(false);

    const requestIdRef = useRef(0);

    const { user } = useUser();

    async function getAIExplanation(questionData, data, force = false, update = true) {
        if (!user || user.isGuest) {
            console.error("User is not authenticated, cannot fetch AI explanation.");
            return;
        }

        if ((loadingAi || aiResponse) && !force) {
            return; // Prevent multiple requests
        }

        if (!data || !questionData) {
            console.error("Missing data for AI explanation");
            return;
        }

        data.question = toPlainText(data.question);
        if (!data.question) {
            console.error("Extracted question text is empty");
            return;
        }

        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;

        setLoadingAi(true);
        setAiResponse(null);

        try {
            let res = await backendFunction("explain", {
                method: "POST",
                body: {
                    questionData: questionData,
                    data: data,
                },
                withAuth: true
            })

            if (res.error) {
                console.error("Error getting AI explanation:", res.error);
                return;
            }

            if (typeof res.data === "string") {
                try {
                    res.data = JSON.parse(res.data);
                } catch (e) {
                    console.error("Error parsing AI explanation response:", e);
                    return;
                }
            }

            if (!res.data || !res.data.message) {
                console.error("AI explanation response is empty or invalid:", res.data);
                return;
            }

            if (requestIdRef.current === currentRequestId && update) {
                setAiResponse(res.data.message);
                setLoadingAi(false);
            } else {
                if (!update) {
                    console.warn("Ignored stale AI response", res.data.message);
                }
            }

            return res.data.message;
        } catch (error) {
            console.error("Error getting AI explanation:", error);
            if (requestIdRef.current === currentRequestId) {
                setLoadingAi(false);
            } return;
        }
    }

    function setExplanation(explanationData) {
        if (!explanationData) {
            return;
        }
        requestIdRef.current += 1;
        setLoadingAi(false);
        setAiResponse(explanationData);
    }


    useImperativeHandle(ref, () => ({
        getAIExplanation,
        setExplanation,
        showSection: () => {
            setShowingSection(true);
        }
    }));

    const aiSectionRef = useRef(null);

    const updateAISection = () => {
        requestAnimationFrame(() => {
            const el = aiSectionRef.current;
            if (!el) return;

            // 1️⃣ Freeze the current height
            const start = el.offsetHeight;      // visible size (padding included)
            el.style.height = `${start}px`;
            el.style.transition = 'none';

            // 2️⃣ Let it expand to natural size, read that
            el.style.height = 'auto';
            const end = el.scrollHeight;        // full content + padding
            // put it back so the transition has somewhere to animate from
            el.style.height = `${start}px`;

            // 3️⃣ Animate to the new size
            requestAnimationFrame(() => {
                el.style.transition = 'height 0.3s ease';
                el.style.height = `${end}px`;
            });
        });
    };

    useLayoutEffect(() => {
        updateAISection();
    }, [aiResponse, loadingAi, showingSection]);

    useEffect(() => {
        const resizeEvent = (e) => {
            if (aiSectionRef.current) {
                updateAISection();
            }
        }
        window.addEventListener('resize', resizeEvent);
        return () => {
            window.removeEventListener('resize', resizeEvent);
        }
    }, []);


    return (
        <>
            {showingSection && (
                <div
                    ref={aiSectionRef}
                    className="text-text bg-background h-0 rounded-xl p-4 text-left text-lg font-normal overflow-hidden transition-all duration-1000 ease-out"
                >
                    {user?.isGuest ? (<h1 className='p-2 text-center font-medium'>You are in guest mode, create an account to use AI features.</h1>) : (loadingAi ? (
                        <div className="flex flex-col gap-2 w-full">
                            {/* Skeleton shimmer bars with slight deviations and uniform lighter shade */}
                            {Array.from({ length: 7 }).map((_, i) => {
                                const widths = [
                                    "w-[98%]", "w-[93%]", "w-[95%]", "w-[97%]", "w-[90%]", "w-[96%]", "w-[92%]"
                                ];
                                return (
                                    <div
                                        key={i}
                                        className={`relative h-4 rounded ${widths[i % widths.length]} bg-background-pop skeleton-shimmer`}
                                    />
                                );
                            })}
                            {/* Shimmer CSS */}
                            <style>
                                {`
                .skeleton-shimmer {
                  position: relative;
                  overflow: hidden;
                }
                .skeleton-shimmer::after {
                  content: "";
                  position: absolute;
                  inset: 0;
                  background: linear-gradient(
                    110deg,
                    rgba(255,255,255,0) 0%,
                    rgba(255,255,255,0.04) 50%,
                    rgba(255,255,255,0) 100%
                  );
                  transform: translateX(-100%);
                  animation: shimmer 1.1s infinite;
                }
                @keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
              `}
                            </style>
                        </div>
                    ) : (
                        <div className="h-fit">
                            <MarkdownRenderer content={aiResponse} />
                        </div>
                    ))}
                </div>
            )}</>
    );


})

export default AIExplanation